// ajaxModal.ts
type ButtonState = 'disable' | 'hidden' | 'enable';
type HeaderState = 'hidden' | 'disable' | 'show';

import { Modal } from 'bootstrap';
import 'bootstrap/dist/js/bootstrap.bundle';

interface ModalConfig {
    url?: string;
    modalTitle?: string;
    actionUrl?: string;
    actionAjaxClass?: string;
    actionAjaxLoadingTarget?: string;
    actionMethod?: string;     // GET/POST/PUT/DELETE...
    actionType?: string;       // napr. "json", "form", atď.
    actionAjax?: boolean;
    actionFormId?: string;     // např. "common-modal-form"
    addClass?: string;
    topPadding?: string;       // "none“ alebo čokoľvek
    buttonLoadingAnimation?: string;
    modalHeaderStatus?: HeaderState;
    buttonSubmitStatus?: ButtonState;
    buttonSubmitText?: string;
    modalSize?: string;        // modal-lg/modal-sm/modal-xl/modal-xs
}

type AjaxResponse =
    | { status: number; redirect?: string; callback?: { legend: number }; [k: string]: unknown }
    | Record<string, unknown>;

interface InitOptions {
    /** voliteľný callback pre vlastné spracovanie odpovede */
    onResponse?: (resp: AjaxResponse) => void;
    /** id hlavičky modalu (default: #dynamic-modal-header) */
    headerSel?: string;
    /** id titulku modalu (default: #dynamic-modal-title) */
    titleSel?: string;
    /** id submit tlačidla (default: #dynamic-modal-submit-button) */
    submitBtnSel?: string;
    /** cieľ výstupu (default: #dynamic-modal-output) */
    outputSel?: string;
    /** overlay načítavania (default: #content-loader-overlay) */
    loaderSel?: string;
    /** id formu (default: #common-modal-form) */
    formSel?: string;
    /** voliteľný selector na modal dialog pre nastavenie veľkosti (ak používaš Bootstrap:.modal-dialog) */
    modalDialogSel?: string;
}

/**
 * Bezpečný výber elementu
 */
function $(selector: string): HTMLElement | null {
    return document.querySelector(selector);
}

function parseBool(v?: string | boolean | null): boolean {
    if (typeof v === 'boolean') return v;
    if (v == null) return false;
    const s = String(v).trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes';
}

function setHeaderState(header: HTMLElement, state?: HeaderState) {
    if (!state) return;
    if (state === 'hidden' || state === 'disable') {
        header.classList.add('d-none', 'invisible');
        header.classList.remove('modal-header');
    }
    if (state === 'show') {
        header.classList.remove('d-none', 'invisible');
        header.classList.add('modal-header');
    }
}

function setButtonState(btn: HTMLButtonElement, state?: ButtonState, text?: string) {
    if (typeof text === 'string') btn.textContent = text;

    if (!state) return;

    if (state === 'disable') {
        btn.disabled = true;
        btn.classList.remove('d-none', 'invisible');
    } else if (state === 'hidden') {
        btn.disabled = true;
        btn.classList.add('d-none', 'invisible');
    } else if (state === 'enable') {
        btn.disabled = false;
        btn.classList.remove('d-none', 'invisible');
        btn.className = 'btn btn-success';
    }
}

function setModalSize(modalDialog: Element | null, size?: string) {
    if (!modalDialog || !size) return;
    // vyčistí predchádzajúce bootstrap veľkosti a nastaví novú
    modalDialog.classList.remove('modal-sm', 'modal-lg', 'modal-xl');
    modalDialog.classList.add(size);
}

function readConfigFrom(el: Element): ModalConfig {
    const d = (el as HTMLElement).dataset;
    return {
        url: d.url,
        modalTitle: d.modalTitle,
        actionUrl: d.actionUrl,
        actionAjaxClass: d.actionAjaxClass,
        actionAjaxLoadingTarget: d.actionAjaxLoadingTarget,
        actionMethod: d.actionMethod ?? 'POST',
        actionType: d.actionType,
        actionAjax: parseBool(d.actionAjax),
        actionFormId: d.actionFormId,
        addClass: d.addClass,
        topPadding: d.topPadding,
        buttonLoadingAnimation: d.buttonLoadingAnimation,
        modalHeaderStatus: (d.modalHeaderStatus as HeaderState) ?? undefined,
        buttonSubmitStatus: (d.buttonSubmitStatus as ButtonState) ?? undefined,
        buttonSubmitText: d.buttonSubmitText,
        modalSize: d.modalSize && d.modalSize !== '' ? d.modalSize : 'modal-lg',
    };
}

async function submitFormAjax(
    form: HTMLFormElement,
    actionUrl: string,
    method = 'POST',
    extraData: Record<string, any> = { ajax: '1' },
    onResponse?: (resp: AjaxResponse) => void,
    loader?: HTMLElement | null,
) {
    // zapni loader (ak máš opačnú logiku, kľudne prehoď)
    if (loader) loader.classList.remove('d-none', 'invisible');

    const fd = new FormData(form);
    Object.entries(extraData).forEach(([k, v]) => fd.append(k, String(v)));

    const resp = await fetch(actionUrl, {
        method,
        body: fd,
        headers: {
            // necháme browser nastaviť boundary pre multipart/form-data
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json, text/html;q=0.9,*/*;q=0.8',
        },
        credentials: 'same-origin',
    });

    // vypni loader s fade efektom “náhradou” (CSS pre transition si vieš pridať)
    if (loader) loader.classList.add('fade-out');

    let data: AjaxResponse;
    const contentType = resp.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
        data = await resp.json();
    } else {
        // fallback – vrátilo HTML / iné
        data = { status: resp.status, body: await resp.text() };
    }

    onResponse?.(data);
}

function closeModal() {
    const modalEl = document.getElementById('modal-dynamic-content') as HTMLElement;
    if (!modalEl) return;

    const bsModal = Modal.getOrCreateInstance(modalEl);
    bsModal.hide();
}

/**
 * Načíta HTML obsah modalu cez GET (controller vracia { success, html }).
 */
async function loadModalContent(url: string, target: HTMLElement, loader?: HTMLElement | null) {
    if (loader) loader.classList.remove('d-none', 'invisible');
    target.innerHTML = '';

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
            credentials: 'same-origin',
        });
        const data = await resp.json();
        if (data && typeof data.html === 'string') {
            target.innerHTML = data.html;
        }
    } catch (err) {
        console.error('[ajaxModal] Nepodarilo sa načítať obsah modalu:', err);
    } finally {
        if (loader) loader.classList.add('d-none');
    }
}

// Dedupe submit handlerov – pri opätovnom otvorení modalu sa starý odpojí.
const boundForms = new WeakMap<HTMLFormElement, EventListener>();

function bindModalFormSubmit(
    form: HTMLFormElement,
    cfg: ModalConfig,
    loader: HTMLElement | null,
    modalEl: HTMLElement,
    onResponse?: (resp: AjaxResponse) => void,
) {
    const prev = boundForms.get(form);
    if (prev) form.removeEventListener('submit', prev);

    const handler: EventListener = (e) => {
        e.preventDefault();
        submitFormAjax(
            form,
            cfg.actionUrl!,
            (cfg.actionMethod || 'POST').toUpperCase(),
            { ajax: '1' },
            (resp) => {
                const r = resp as any;

                if (r?.success === true) {
                    // Notifikuj stránku (napr. menu builder prekreslí PDF náhľad).
                    document.dispatchEvent(new CustomEvent('foodilo:modal-saved', { detail: r }));
                    Modal.getOrCreateInstance(modalEl).hide();
                }

                onResponse?.(r);
            },
            loader,
        ).catch((err) => console.error('[ajaxModal] AJAX submit error:', err));
    };

    form.addEventListener('submit', handler);
    boundForms.set(form, handler);
}

export function initAjaxModal(options: InitOptions = {}) {
    const {
        onResponse,
        headerSel = '#dynamic-modal-header',
        titleSel = '#dynamic-modal-title',
        submitBtnSel = '#dynamic-modal-submit-button',
        outputSel = '#dynamic-modal-output',
        loaderSel = '#content-loader-overlay',
        formSel = '#common-modal-form',
        modalDialogSel = '.modal-dialog',
    } = options;

    document.addEventListener('click', (ev) => {

        const target = ev.target as HTMLElement | null;
        if (!target) return;

        const button = target.closest('.edit-add-modal-button') as HTMLElement | null;
        if (!button) return;

        ev.preventDefault();

        // načítaj config z data-* atribútov
        const cfg = readConfigFrom(button);

        // prvky modalu
        const header = $(headerSel);
        const title = $(titleSel);
        const submitBtn = $(submitBtnSel) as HTMLButtonElement | null;
        const output = $(outputSel);
        const loader = $(loaderSel);
        const modalDialog = document.querySelector(modalDialogSel);

        if (header) setHeaderState(header, cfg.modalHeaderStatus);
        if (title && typeof cfg.modalTitle === 'string') title.textContent = cfg.modalTitle;
        if (submitBtn) setButtonState(submitBtn, cfg.buttonSubmitStatus, cfg.buttonSubmitText);
        setModalSize(modalDialog, cfg.modalSize || 'modal-lg');

        // ak si chceš niečo pridať do kontajnera s obsahom
        if (output && cfg.addClass) output.classList.add(...cfg.addClass.split(' ').filter(Boolean));

        const element = document.getElementById('modal-dynamic-content') as HTMLElement;
        const myModal = new Modal(element);
        myModal.show();

        // Načítaj obsah modalu (GET) do tela modalu.
        const bodyTarget = document.getElementById('dynamic-modal-body');
        if (cfg.url && bodyTarget) {
            void loadModalContent(cfg.url, bodyTarget, loader);
        }

        // AJAX submit formulára – naviaž, ak máme kam odoslať.
        if (cfg.actionUrl) {
            const formId = cfg.actionFormId ? `#${cfg.actionFormId}` : formSel;
            const form = document.querySelector(formId) as HTMLFormElement | null;

            if (!form) {
                console.warn(`[ajaxModal] Formulár ${formId} neexistuje.`);
                return;
            }

            form.setAttribute('action', cfg.actionUrl);
            bindModalFormSubmit(form, cfg, loader, element, onResponse);
        }
    });

    // naviazanie handlera
    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;

        if (target.closest('#dynamic-modal-dismiss-button')) {
            e.preventDefault();
            closeModal();
        }
    });
}
